/**
 * VoucherFreeGoodsRow Component
 * Display voucher free goods item with voucher info
 * Similar to promo free goods but with voucher-specific styling
 */

import { AspectRatio, Box, Flex } from '@chakra-ui/react';
import { CardPromoInfo, Text } from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';

import { Image } from '../../common/image';
import type { AppliedVoucherInfo, VoucherProductFreeGood } from './types';

export interface VoucherFreeGoodsRowProps {
  freeGood: VoucherProductFreeGood;
  voucherInfo?: AppliedVoucherInfo;
}

/**
 * Format voucher period to display string
 * e.g. "1 Juni - 31 Juli 2026 • 15:59 WIB"
 */
function formatVoucherPeriod(
  periodStart?: string,
  periodEnd?: string
): string | null {
  if (!periodStart || !periodEnd) return null;

  try {
    const startDate = new Date(periodStart);
    const endDate = new Date(periodEnd);

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    };

    const formatTime = (date: Date) => {
      return date.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)} • ${formatTime(
      endDate
    )}`;
  } catch {
    return null;
  }
}

/**
 * Extract product image URL from product_images array
 * Handles both string[] and object[] formats
 */
function getProductImage(
  images?: VoucherProductFreeGood['product_images']
): string {
  if (!images || images.length === 0) return '';

  const firstImage = images[0];

  // If it's a string, return directly
  if (typeof firstImage === 'string') {
    return firstImage;
  }

  // If it's an object, try to get the best quality image
  return (
    firstImage.medium_image_url ||
    firstImage.image ||
    firstImage.thumbnail_url ||
    ''
  );
}

export function VoucherFreeGoodsRow({
  freeGood,
  voucherInfo,
}: VoucherFreeGoodsRowProps) {
  // Quantity from backend is magnified x1000
  const quantity = (freeGood.quantity || 0) / 1000;
  const productImage = getProductImage(freeGood.product_images);
  const period = formatVoucherPeriod(
    voucherInfo?.period_start,
    voucherInfo?.period_end
  );

  const displayText = `Voucher ${
    voucherInfo?.voucher_code ?? ''
  }: Gratis ${quantity} ${freeGood.product_name || ''}`.trim();

  // Extract all images for CardPromoInfo
  const allImages = (freeGood.product_images || [])
    .map((img) =>
      typeof img === 'string' ? img : img.medium_image_url || img.image || ''
    )
    .filter(Boolean);

  return (
    <CardPromoInfo
      type="free-item"
      variant="checkout"
      duration={period || ''}
      text={displayText}
      images={allImages}
      savings={freeGood.saved_amount}
      showFreeItemPrice={false}
      showVoucherIcon
      item={{
        id: freeGood.product_item_code?.toString() || '',
        img: productImage,
        price:
          freeGood.saved_amount && quantity > 0
            ? freeGood.saved_amount / quantity
            : 0,
        qty: quantity,
        uom: 'pcs',
      }}
    />
  );
}
